import { useCallback, useState } from 'react';
import { useKeycloak } from '../../authentication/keycloak/KeycloakProvider';
import {
  cancelWorkplaceRetirement,
  scheduleWorkplaceRetirement,
  type WorkplaceRetirement,
  type WorkplaceRetirementFailure,
} from '../services/floorplan-editor.service';

export interface WorkplaceRetirementState {
  readonly saving: boolean;
  readonly error?: WorkplaceRetirementFailure;
  /** Schedules the retirement; resolves to the new state, or undefined when it was refused. */
  readonly schedule: (
    workplaceId: string,
    retiresOn: string,
    allowInsideBookingWindow?: boolean,
  ) => Promise<WorkplaceRetirement | undefined>;
  /** Clears a scheduled retirement. */
  readonly cancel: (workplaceId: string) => Promise<WorkplaceRetirement | undefined>;
  readonly clearError: () => void;
}

/**
 * Scheduling and cancelling a desk's withdrawal from service.
 *
 * Separate from the layer save on purpose. Retirement is a property of the desk record, not of
 * the drawing, and folding it into the plan save would make withdrawing a desk fail whenever
 * the plan happened to have an unrelated validation problem — and would put it behind an
 * optimistic-concurrency check meant for two humans editing geometry.
 */
export const useWorkplaceRetirement = (): WorkplaceRetirementState => {
  const keycloak = useKeycloak();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<WorkplaceRetirementFailure | undefined>();

  const clearError = useCallback(() => setError(undefined), []);

  const schedule = useCallback(
    async (
      workplaceId: string,
      retiresOn: string,
      allowInsideBookingWindow = false,
    ): Promise<WorkplaceRetirement | undefined> => {
      setSaving(true);
      setError(undefined);

      try {
        const result = await scheduleWorkplaceRetirement(workplaceId, retiresOn, keycloak, allowInsideBookingWindow);

        if (result.status !== 'ok') {
          setError(result.status);
          return undefined;
        }

        return result.data;
      } catch {
        setError('failed');
        return undefined;
      } finally {
        setSaving(false);
      }
    },
    [keycloak],
  );

  const cancel = useCallback(
    async (workplaceId: string): Promise<WorkplaceRetirement | undefined> => {
      setSaving(true);
      setError(undefined);

      try {
        const result = await cancelWorkplaceRetirement(workplaceId, keycloak);

        if (result.status !== 'ok') {
          setError(result.status);
          return undefined;
        }

        return result.data;
      } catch {
        setError('failed');
        return undefined;
      } finally {
        setSaving(false);
      }
    },
    [keycloak],
  );

  return { saving, error, schedule, cancel, clearError };
};

export default useWorkplaceRetirement;
